import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { incidentErrorTypeService, type IncidentErrorType } from '@/api/domains/catalog';
import { capitalizeFirst, formatDateToYYYYMMDD } from '@/utils';
import { employeeIncidentService } from '@/api/domains/employeeIncident';
import type {
  IncidentItem,
  IncidentTotals,
  IncidentStatus,
} from '@/api/domains/employeeIncident';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { IndicatorCardVariant } from '@/components/ui/cards';
import { useMyIncidentsExport } from './useMyIncidentsExport';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';


export interface IncidentStatusCounter {
  key: IncidentStatus;
  label: string;
  value: number;
  variant: IndicatorCardVariant;
  isSelected: boolean;
}

interface StatusCounterDefinition {
  statusKey: IncidentStatus;
  totalsKey: keyof IncidentTotals;
  variant: IndicatorCardVariant;
}

// Orden de despliegue fijo, con el color asociado a cada estado (ASIS-5604).
const STATUS_COUNTER_DEFINITIONS: StatusCounterDefinition[] = [
  { statusKey: 'todos', totalsKey: 'todos', variant: 'primary' },
  { statusKey: 'pendiente', totalsKey: 'pendientes', variant: 'alert' },
  { statusKey: 'aprobado', totalsKey: 'aprobados', variant: 'success' },
  { statusKey: 'rechazado', totalsKey: 'rechazados', variant: 'error' },
  { statusKey: 'error', totalsKey: 'error', variant: 'important' },
];


export interface IncidentListFilters {
  from?: string;
  to?: string;
  errorTypeId: number | null;
  status: IncidentStatus;
}

export const useMyIncidentsList = () => {
  const { t: translate } = useTranslation('attendance');
  const { showNotification, showApiError, openSidePanel } = useUI();
  const employeeId = useSelector((state: RootState) => state.auth.user?.idEmpleado);

  const incidentErrorTypesCatalog = useCatalogLoader<IncidentErrorType>(
    (signal) => incidentErrorTypeService.getIncidentErrorTypes(signal),
    (type) => ({
      value: String(type.id),
      label: type.description,
    }),
    [],
  );

  const defaultFrom = useMemo(() => {
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 1);
    return formatDateToYYYYMMDD(fromDate);
  }, []);

  const defaultTo = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  const [totals, setTotals] = useState<IncidentTotals>({
    pendientes: 0,
    aprobados: 0,
    rechazados: 0,
    error: 0,
    todos: 0,
  });

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<IncidentListFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<IncidentItem> | null> => {
      if (!employeeId) return null;

      const fromDateString = params.filters.from ?? defaultFrom;
      const toDateString = params.filters.to ?? defaultTo;

      const response = await employeeIncidentService.list({
        employeeId,
        startDate: fromDateString,
        endDate: toDateString,
        errorTypeId: params.filters.errorTypeId,
        status: params.filters.status,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText || null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('incidents.errors.loadFailed'));
        setTotals({ pendientes: 0, aprobados: 0, rechazados: 0, error: 0, todos: 0 });
        return null;
      }

      setTotals(response.data.totals);
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [employeeId, defaultFrom, defaultTo, showApiError, translate],
  );

  const {
    rows: data,
    total: totalRecords,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
  } = usePaginatedList<IncidentItem, IncidentListFilters>({
    fetchFn,
    initialFilters: {
      from: defaultFrom,
      to: defaultTo,
      errorTypeId: null,
      status: 'todos',
    },
    initialSort: { key: 'createdAt', direction: 'DESC' },
    orderByMap: {
      id: 'createdAt',
      errorType: 'errorType',
      incidentDate: 'createdAt',
      checkInDate: 'checkInDate',
      status: 'status',
    },
    enabled: Boolean(employeeId),
    screenCode: ROUTE_DEFINITIONS.MIS_INCIDENTES.code,
  });

  // Export hook
  const { exportIncidents, isExporting } = useMyIncidentsExport({
    startDate: filters.from ?? defaultFrom,
    endDate: filters.to ?? defaultTo,
    errorTypeId: filters.errorTypeId,
    status: filters.status,
  });

  const handleFilterChange = useCallback((key: string, value: string | string[]) => {
    const parsedValue = key === 'errorTypeId' && typeof value === 'string'
      ? (value === '0' ? null : Number(value))
      : value;
    setFilters((previous) => ({ ...previous, [key]: parsedValue }));
  }, [setFilters]);

  // Filter options for dropdowns
  const errorTypeOptions = useMemo(() => [
    { value: '0', label: translate('incidents.filters.allErrorTypes') },
    ...incidentErrorTypesCatalog.options.map((option) => ({
      ...option,
      label: capitalizeFirst(option.label),
    })),
  ], [incidentErrorTypesCatalog.options, translate]);

  const filterDefinitions = useMemo(
    () => [
      {
        key: 'errorTypeId',
        label: translate('incidents.filters.errorType'),
        options: errorTypeOptions,
        defaultValue: '0',
      },
    ],
    [translate, errorTypeOptions],
  );

  // Tarjetas contadoras por estado (ASIS-5604, AC3/AC4): "Todos" siempre expone el total real
  // (igual que en Aprobación HHEE); las tarjetas de estado específico solo muestran su total
  // cuando no hay filtro puntual (status === 'todos') o cuando son la tarjeta seleccionada — en
  // cualquier otro caso quedan en cero porque ese subtotal no aplica a la vista ya filtrada.
  const counters: IncidentStatusCounter[] = useMemo(
    () => STATUS_COUNTER_DEFINITIONS.map(({ statusKey, totalsKey, variant }) => ({
      key: statusKey,
      label: translate(`incidents.indicators.${totalsKey}`),
      value: statusKey === 'todos' || filters.status === 'todos' || filters.status === statusKey
        ? totals[totalsKey]
        : 0,
      variant,
      isSelected: filters.status === statusKey,
    })),
    [translate, totals, filters.status],
  );

  const onCounterSelect = useCallback((key: IncidentStatus) => {
    setFilters((previous) => ({ ...previous, status: key }));
  }, [setFilters]);

  // Table columns
  const columns: TableColumn<IncidentItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('incidents.table.columns.id'),
        sortable: true,
      },
      {
        key: 'errorType',
        header: translate('incidents.table.columns.errorType'),
        sortable: true,
      },
      {
        key: 'incidentDate',
        header: translate('incidents.table.columns.incidentDate'),
        sortable: true,
      },
      {
        key: 'checkInDate',
        header: translate('incidents.table.columns.checkInDate'),
        sortable: true,
      },
      {
        key: 'checkInTime',
        header: translate('incidents.table.columns.checkInTime'),
        sortable: false,
      },
      {
        key: 'location',
        header: translate('incidents.table.columns.location'),
        sortable: false,
      },
      {
        key: 'status',
        header: translate('incidents.table.columns.status'),
        sortable: true,
      },
    ],
    [translate],
  );

  const handleViewDetail = useCallback(
    async (row: IncidentItem) => {
      if (!employeeId) return;

      const response = await employeeIncidentService.getDetail(employeeId, row.id);

      if (!response.success || !response.data) {
        const detail = response.error?.validationErrors
          ?.map((validationError) => `${validationError.fieldName}: ${validationError.errorMessage}`)
          .join('; ');
        const message = [response.error?.description, detail]
          .filter(Boolean)
          .join(' — ');
        showNotification('error', message || translate('incidents.errors.loadDetailFailed'), 5000);
        return;
      }

      openSidePanel({
        component: 'IncidentDetailForm',
        title: translate('incidents.detail.title'),
        props: { incident: response.data },
      });
    },
    [employeeId, openSidePanel, showNotification, translate],
  );

  const actions: TableAction<IncidentItem>[] = useMemo(
    () => [
      {
        id: 'view-detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye' as const,
        onClick: handleViewDetail,
      },
    ],
    [translate, handleViewDetail],
  );

  // Formatted filter values for FilterDropdown
  const formattedFilterValues = useMemo(
    () => ({
      errorTypeId: filters.errorTypeId !== null ? String(filters.errorTypeId) : '0',
    }),
    [filters.errorTypeId],
  );

  return {
    // Filters
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    filterDefinitions,
    formattedFilterValues,
    handleFilterChange,
    // Data
    data,
    totals,
    counters,
    onCounterSelect,
    totalRecords,
    isLoading,
    // Pagination
    page,
    pageSize,
    setPage,
    setPageSize,
    // Search & Sort
    onSearch,
    onSortChange,
    // Table config
    columns,
    actions,
    // Export
    exportIncidents,
    isExporting,
    // i18n
    translate,
  };
};
